import { Button, Spin } from 'lego-on-react';
import block from 'propmods';
import React, { FC, useEffect, useState } from 'react';
import { moment } from '../../lib/moment';
import { SoundResource } from '../../model/resources';
import CopyField from '../CopyField/CopyField';
import Link from '../Link/Link';
import { ItemWrapper, ResourceError } from './ResourceManager';
import { formatBytes } from '../../lib/utils';
import { getPoller } from './Poller';
import { getSkillSound } from '../../api';
import { PS } from '../PS';

import './SoundTable.scss';

const b = block('SoundTable');

interface Props {
    remove: (id: string) => Promise<void>;
    items: Array<ItemWrapper<SoundResource>>;
    skillId: string;
}

const DocLink: FC = ({ children }) => {
    return (
        <Link to="https://yandex.ru/dev/dialogs/alice/doc/resource-sounds-upload-docpage" target="_blank">
            {children}
        </Link>
    );
};

const SoundError: React.FC<{ error: ResourceError }> = ({ error }) => {
    if (!error.message) {
        return <p>Произошла ошибка</p>;
    }

    if (error.message.indexOf('Quota exceeded') > -1) {
        return <DocLink>Превышена квота на звуки</DocLink>;
    }

    switch (error.message) {
        case 'File is too large':
            return <DocLink>Превышен размер файла</DocLink>;
        default:
            return <p>Произошла ошибка</p>;
    }
};

const SoundTable: FC<Props> = (props) => {
    const { items, remove, skillId } = props;
    const { staticResourcesUrl } = __appConfig.app;
    const [_, setState] = useState(0);

    useEffect(() => {
        const inProcess = items.filter((x) => x.item && !x.item.isProcessed && !x.item.error);
        const stop = getPoller(
            inProcess,
            (s) => getSkillSound(skillId, s),
            () => setState(new Date().getTime()),
        );

        return stop;
    }, [items, skillId]);

    return (
        <table {...b('items')} cellSpacing="0">
            <tbody>
                {items.map(({ item, error }, i) =>
                    item ? (
                        <tr key={`${item.id}_${item.isProcessed}_${item.error}`} {...b('item')}>
                            <td {...b('item-sound')}>
                                <div {...b('sound')}>
                                    {item.isProcessed ? (
                                        <PS.Audio>
                                            <source
                                                src={`${staticResourcesUrl}/dialogs/dialogs-upload/sounds/opus/${skillId}/${item.id}.opus`}
                                                type="audio/ogg; codecs=opus"
                                            />
                                        </PS.Audio>
                                    ) : item.error ? (
                                        <div {...b('error')}>!</div>
                                    ) : (
                                        <Spin size="m" progress />
                                    )}
                                </div>
                            </td>
                            <td {...b('item-createdAt')}>
                                <p>{moment(item.createdAt).format('DD.MM.YYYY')}</p>
                                <p>{item.size ? formatBytes(item.size) : null}</p>
                                <p {...b('fileName')}>{item.originalName}</p>
                            </td>
                            <td {...b('item-code')}>
                                {item.isProcessed ? (
                                    <CopyField
                                        text={`<speaker audio="dialogs-upload/${skillId}/${item.id}.opus">`}
                                        buttonLabel="Скопировать"
                                        label=""
                                        border={false}
                                    />
                                ) : item.error ? (
                                    <p>
                                        Произошла ошибка при обработке звукового файла, попробуйте загрузить другой файл
                                    </p>
                                ) : (
                                    <p>Звуковая дорожка в обработке, пожалуйста подождите</p>
                                )}
                            </td>
                            <td {...b('item-actions')}>
                                <Button theme="normal" size="s" onClick={() => remove(item.id)}>
                                    Удалить
                                </Button>
                            </td>
                        </tr>
                    ) : (
                        <tr key={i} {...b('item', { uploading: true })}>
                            <td {...b('item-sound')}>
                                {error ? <div {...b('error')}>!</div> : <Spin size="m" progress />}
                            </td>
                            <td {...b('item-createdAt')} />
                            <td {...b('item-code')}>{error ? <SoundError error={error} /> : null}</td>
                            <td {...b('item-actions')} />
                        </tr>
                    ),
                )}
            </tbody>
        </table>
    );
};

export default SoundTable;
